import type { PGD } from "../types";

export const pgdClarithromycinImpetigo: PGD = {
  id: "pgd-3d-clari-impetigo",
  reference: "3d",
  version: "1.0",
  drugRef: "clarithromycin",
  indication: "Widespread non-bullous impetigo — penicillin allergic, not pregnant",
  validFrom: "2024-01-31",
  expiryDate: "2027-01-30",
  frameworkType: "pharmacy_first",
  population: {
    ageRange: { min: 1, max: 999 },
    pregnancyAllowed: false,
    breastfeedingAllowed: true,
    notes: "Children 1–11 require weight for dosing. Widespread non-bullous impetigo only.",
  },
  duration: { days: 5 },
  requiresWeightInput: { min: 1, max: 11 },
  doseBands: [
    {
      id: "clari-under8kg",
      resolveBy: "weight",
      weightRange: { minKg: 0, maxKg: 7.99 },
      dose: 0,
      mgPerKg: 7.5,
      frequency: "TWICE a day",
      comment: "Formula band: dose mg = weight × 7.5 mg/kg.",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give {volume} ({dose}mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give {volume} ({dose}mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-8-11kg",
      resolveBy: "weight",
      weightRange: { minKg: 8, maxKg: 11.99 },
      dose: 62.5,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 1.25mL (62.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 2.5mL (62.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-12-19kg",
      resolveBy: "weight",
      weightRange: { minKg: 12, maxKg: 19.99 },
      dose: 125,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 2.5mL (125mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 5mL (125mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
      ],
    },
    {
      id: "clari-20-29kg",
      resolveBy: "weight",
      weightRange: { minKg: 20, maxKg: 29.99 },
      dose: 187.5,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 3.75mL (187.5mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 7.5mL (187.5mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
      ],
    },
    {
      id: "clari-30-40kg",
      resolveBy: "weight",
      weightRange: { minKg: 30, maxKg: 40 },
      dose: 250,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-250-tab",
          instruction: "Take ONE 250mg tablet TWICE a day for 5 days",
          quantity: { count: 10, label: "10 tablets" },
        },
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 5mL (250mg) TWICE a day for 5 days",
          quantity: { bottles: 1, label: "1 × 70mL bottle" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 10mL (250mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
      ],
    },
    {
      id: "clari-12plus",
      resolveBy: "age",
      ageRange: { min: 12, max: 999 },
      dose: 500,
      frequency: "TWICE a day",
      formulations: [
        {
          formulationId: "clari-500-tab",
          instruction: "Take ONE 500mg tablet TWICE a day for 5 days",
          quantity: { count: 10, label: "10 tablets" },
        },
        {
          formulationId: "clari-250-tab",
          instruction: "Take TWO 250mg tablets TWICE a day for 5 days",
          quantity: { count: 20, label: "20 tablets" },
        },
        {
          formulationId: "clari-250-5ml",
          instruction: "Give 10mL (500mg) TWICE a day for 5 days",
          quantity: { bottles: 2, label: "2 × 70mL bottles" },
        },
        {
          formulationId: "clari-125-5ml",
          instruction: "Give 20mL (500mg) TWICE a day for 5 days",
          quantity: { bottles: 3, label: "3 × 70mL bottles" },
        },
      ],
    },
  ],
  preSupplyChecks: [
    { id: "ex-consent-refused", question: "Has the patient/carer refused consent?" },
    {
      id: "ex-unable-swallow",
      question:
        "Unable to absorb oral medications and/or unable to swallow oral dosage formulations?",
    },
    { id: "ex-macrolide-allergy", question: "Allergy to clarithromycin or any macrolide?" },
    {
      id: "ex-long-term-macrolide",
      question: "Currently on long-term clarithromycin or another macrolide antibiotic?",
      hint: "e.g. erythromycin for asplenia prophylaxis, azithromycin for COPD/bronchiectasis",
    },
    { id: "ex-failed-abx", question: "Failed previous oral antimicrobial treatment for this episode of impetigo?" },
    { id: "ex-keto", question: "Ketogenic diet?" },
    { id: "ex-myasthenia", question: "Known myasthenia gravis?" },
    {
      id: "ex-qt",
      question: "History of QT prolongation (congenital or acquired) or ventricular cardiac arrhythmia including torsades de pointes?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    {
      id: "ex-qt-meds",
      question: "Taking any medicine known to cause QT prolongation?",
      linkRefs: ["crediblemeds-qt", "sads-drugs-avoid"],
    },
    { id: "ex-electrolyte", question: "Known electrolyte disturbances (hypokalaemia or hypomagnesaemia)?" },
    { id: "ex-ckd5", question: "Known CKD stage 5 (eGFR <15 mL/min/1.73m²)?" },
    { id: "ex-macrolide-jaundice", question: "Previous history of macrolide-associated jaundice or hepatic dysfunction?" },
    { id: "ex-severe-liver", question: "Severe liver disease?" },
    { id: "ex-typhoid", question: "Oral typhoid vaccine within ±3 days?" },
    { id: "int-simvastatin", question: "Taking simvastatin or lovastatin?" },
    { id: "int-ergot", question: "Taking ergotamine or dihydroergotamine?" },
    { id: "int-cisapride-pimozide", question: "Taking astemizole, cisapride, domperidone, pimozide, or terfenadine?" },
    { id: "int-ranolazine", question: "Taking ranolazine?" },
    { id: "int-ticagrelor", question: "Taking ticagrelor?" },
    { id: "int-colchicine", question: "Taking colchicine?" },
    { id: "int-midazolam-oral", question: "Taking oral midazolam?" },
    {
      id: "int-cyp-inducers",
      question: "Taking strong CYP3A4 inducers?",
      hint: "efavirenz, etravirine, nevirapine, rifampicin, rifabutin, rifapentine, phenytoin, carbamazepine, phenobarbital, St John's wort",
      linkRefs: ["indiana-flockhart"],
    },
  ],
  cautions: [
    { id: "caution-breastfeeding", text: "Breastfeeding — monitor nursing infant for GI disturbances, oral candida, rashes, drowsiness, irritability, sweating, loss of appetite" },
    { id: "caution-warfarin", text: "Coumarin anticoagulants — INR may rise. Advise INR monitoring and to seek attention if bleeding." },
    { id: "caution-doac", text: "DOACs — increased risk of bleeding. Advise to seek attention if bleeding." },
    { id: "caution-statins", text: "Statins — counsel on rhabdomyolysis risk. Consider withholding statin." },
    { id: "caution-ccb", text: "Calcium channel blockers — risk of hypotension. Advise avoid driving if dizzy." },
    { id: "caution-hypoglycaemics", text: "Oral hypoglycaemics/insulin — risk of hypoglycaemia. Advise more frequent blood glucose monitoring." },
    { id: "caution-digoxin", text: "Digoxin — may increase levels. Advise on symptoms of toxicity." },
    { id: "caution-hypokalaemia", text: "Medicines causing hypokalaemia — electrolyte disturbance risk. Advise to contact prescriber." },
    { id: "caution-excipients", text: "Check excipients: lactose, sucrose, fructose, sorbitol; aspartame in phenylketonuria" },
  ],
  counselling: [
    "Can be taken with or without food",
    "Tablets should be swallowed whole with water",
    "Complete the full 5-day course even if symptoms improve",
    "Return to pharmacy if symptoms worsen rapidly at any time",
  ],
  counsellingPoints: [
    { text: "Can be taken with or without food" },
    { text: "Tablets should be swallowed whole with water" },
    { text: "Complete the full 5-day course even if symptoms improve" },
    { text: "Manage discomfort with appropriate OTC pain relief (paracetamol or ibuprofen where appropriate)" },
    { text: "Return to pharmacy if symptoms worsen rapidly at any time" },
    { text: "Return if no improvement after completing the course" },
    { text: "Seek urgent help if signs of sepsis develop — call 999 or attend A&E" },
    { text: "Oral suspension may taste bitter — drinking juice or water soon after can help" },
    {
      text: "Children should be encouraged to swallow solid oral dose forms where possible",
      linkRefs: ["medicines-for-children-clari", "medicines-for-children-giving-tablets"],
    },
  ],
  outcomeLinkRefs: ["bad-impetigo-leaflet", "nhs-impetigo"],
};
